import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaUsers, FaLightbulb, FaChartLine, FaHandshake, FaPaperPlane } from 'react-icons/fa';
import emailjs from '@emailjs/browser';

export const AboutPageContainer = styled.div`
  padding: 8rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

export const SectionTitle = styled.h2`
  font-size: 3.5rem;
  color: #1f2937;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #00B4DB, #007bff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

export const SectionSubtitle = styled.p`
  font-size: 1.4rem;
  color: #4b5563;
  margin-bottom: 4rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  text-align: center;
`;

export const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2.5rem;
  margin-bottom: 4rem;
`;

export const AboutCard = styled(motion.div)`
  background: white;
  padding: 2.5rem;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 8px 30px rgba(0, 180, 219, 0.2);
  }

  svg {
    font-size: 3rem;
    color: #00B4DB;
    margin-bottom: 1.5rem;
  }

  h3 {
    font-size: 1.8rem;
    color: #1f2937;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.1rem;
    color: #4b5563;
    line-height: 1.7;
  }
`;

export const TeamSection = styled.section`
  margin-top: 6rem;
  text-align: center;
`;

export const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2.5rem;
  margin-top: 3rem;
`;

export const TeamMember = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  text-align: center;

  img {
    width: 150px;
    height: 150px;
    border-radius: 50%;
    margin-bottom: 1.5rem;
    object-fit: cover;
  }

  h3 {
    font-size: 1.5rem;
    color: #1f2937;
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 1.1rem;
    color: #4b5563;
    margin-bottom: 1rem;
  }

  span {
    font-size: 0.9rem;
    color: #00B4DB;
  }
`;

export const ContactSection = styled.section`
  padding: 4rem 2rem;
  background: linear-gradient(45deg, #00B4DB, #007bff);
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 250px;
  color: white;
  position: relative;
  overflow: hidden;
  margin-top: 8rem;
  border-radius: 20px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 30%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.15), transparent);
    transform: skewX(-20deg) translateX(30%);
    transform-origin: top right;
    pointer-events: none;
    z-index: 0;
  }
`;

export const ContactContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 600px;
  width: 100%;
`;

export const ContactTitle = styled.h2`
  font-size: 2.5rem;
  color: white;
  margin-bottom: 2rem;
`;

export const ContactForm = styled.form`
  display: flex;
  gap: 1rem;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
`;

export const ContactInput = styled.input`
  padding: 1rem 1.5rem;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  flex: 1;
  min-width: 200px;
  background: rgba(255, 255, 255, 0.9);
  color: #1f2937;

  &::placeholder {
    color: #6b7280;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

export const SubmitButton = styled(motion.button)`
  background: #00B4DB;
  color: white;
  border: none;
  padding: 1rem 1.5rem;
  font-size: 1.2rem;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 180, 219, 0.2);

  &:hover {
    background: #007bff;
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 180, 219, 0.3);
  }

  svg {
    margin: 0;
  }
`;

function AboutPage() {
  const [quickContactData, setQuickContactData] = useState({
    name: '',
    phone: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      const numbersOnly = value.replace(/[^\d]/g, '');
      if (numbersOnly.length <= 11) {
        setQuickContactData({
          ...quickContactData,
          [name]: numbersOnly
        });
      }
    } else {
      setQuickContactData({
        ...quickContactData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (quickContactData.phone.length !== 11) {
      alert('Telefon numarası 11 haneli olmalıdır.');
      return;
    }

    try {
      const templateParams = {
        name: quickContactData.name,
        phone: quickContactData.phone,
        time: new Date().toLocaleString('tr-TR')
      };

      await emailjs.send(
        'service_771tgg7',
        'template_w676du8',
        templateParams,
        'xjG6_a9zdp6ZU1zHp'
      );

      alert('Başarılı');
      setQuickContactData({ name: '', phone: '' });
    } catch (error) {
      alert('Bir hata oluştu');
    }
  };

  const values = [
    {
      icon: <FaLightbulb />,
      title: "Yenilikçilik",
      description: "Dijital dünyadaki en son teknolojileri ve trendleri takip ederek, müşterilerimize en yenilikçi çözümleri sunuyoruz."
    },
    {
      icon: <FaChartLine />,
      title: "Sürdürülebilir Büyüme",
      description: "Müşterilerimizin dijital varlıklarını güçlendirerek, uzun vadeli ve sürdürülebilir bir büyüme sağlıyoruz."
    },
    {
      icon: <FaHandshake />,
      title: "Güvenilirlik",
      description: "Şeffaf iletişim ve dürüst yaklaşımımızla, müşterilerimizle güvene dayalı uzun soluklu ilişkiler kuruyoruz."
    }
  ];
  
  return (
    <AboutPageContainer>
      <SectionTitle>Hakkımızda</SectionTitle>
      <SectionSubtitle>
        2002 yılından bu yana bilişim sektöründe edindiğimiz bilgi ve birikimle dijital dünyada markanızı bir adım öne taşımak için yanınızdayız. Modern çözümler ve yenilikçi yaklaşımlarla işinizi büyütüyoruz.
      </SectionSubtitle>

      <AboutGrid>
        {values.map((value, index) => (
          <AboutCard
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            {value.icon}
            <h3>{value.title}</h3>
            <p>{value.description}</p>
          </AboutCard>
        ))}
      </AboutGrid>

      <ContactSection>
        <ContactContent>
          <ContactTitle>Sizi Arayalım</ContactTitle>
          <ContactForm onSubmit={handleSubmit}>
            <ContactInput 
              type="text" 
              name="name"
              placeholder="Adınız"
              value={quickContactData.name}
              onChange={handleInputChange}
              required 
            />
            <ContactInput 
              type="tel" 
              name="phone"
              placeholder="Telefon Numaranız (11 Haneli)"
              value={quickContactData.phone}
              onChange={handleInputChange}
              pattern="[0-9]*"
              inputMode="numeric"
              maxLength="11"
              minLength="11"
              required 
            />
            <SubmitButton
              type="submit"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaPaperPlane />
            </SubmitButton>
          </ContactForm>
        </ContactContent>
      </ContactSection>
    </AboutPageContainer>
  );
}

export default AboutPage; 